import includes from 'lodash/includes';
import isObject from 'lodash/isObject';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy'
import { getLocaleFromLanguageCode } from '../../../intl-helpers';
import get from 'lodash/get';
import { logAnalytic, logAssetDownloadAnalytic } from '../../../api';
import find from 'lodash/find';

const IGNORED_KEYS = [
    'nome_produttore',
    'codice_articolo_produttore',
    'descrizione_articolo_produttore',
    'descrizione_corta_produttore',
]

const TYPE_ATTR = 'type'
const CODICE_ARTICOLO_ATTR = 'codice_articolo'


export const getFileName = (fileName) => {
    if (fileName !== null) {
        return fileName.split(/([\\/])/g).pop();
    }
}

export const getArticleDescription = (data, language) => {
    const identificazione = find(data, { property: 'identificazione' });

    if (!identificazione) {
        return '-';
    }

    const description = find(identificazione.children, { property: 'descrizione_articolo' })

    if (!description) {
        return '-'
    }

    return get(description, `value.${getLocaleFromLanguageCode(language)}`, '-')
}

export const logDownload = (props, user, filename) => {
    //console.log('logDownload', props, user, filename)
    const { articleDetails } = props;

    const locale = getLocaleFromLanguageCode(props.i18n.language);

    const logData = {
        utente: `${user.nome} ${user.cognome}`,
        brand: get(articleDetails, `identificazione.sigla_marca.value.label.${locale}`),
        acronym: get(articleDetails, 'identificazione.sigla_marca.value.code'),
        code: get(articleDetails, 'identificazione.codice_articolo.value'),
        code_description: get(
            articleDetails,
            `identificazione.descrizione_articolo.value.${locale}`
        ),
        operation: 'Download asset',
        subject: filename,
    };

    logAnalytic(logData);
}

/**
 * Log per il download di un asset dalla sezione Digital Assets
 * Sempre attivo, non soggetto a parametro clientLogEnabled
 * @param {Object} articleDetails - dettagli dell'articolo
 * @param {Object} asset - l'asset scaricato
 * @param {string} locale - locale corrente (es. 'it_IT')
 */
export const logAssetDownload = (articleDetails, asset, locale = 'it_IT') => {
    if (!articleDetails || !asset) return;

    // Estrae il nome del file dall'asset
    const fileName = getFileName(asset.filename ?? asset.url) ?? 'unknown';

    // Estrae il tipo di file:
    // - Asset correlati: usa asset_class_description (es. "Certificato")
    // - Asset articolo: usa asset_classes[0].label per la locale (es. "Catalogo")
    const fileType = asset.asset_class_description ??
        asset.asset_classes?.[0]?.label?.[locale] ??
        'unknown';

    const logData = {
        brand: get(articleDetails, `identificazione.sigla_marca.value.label.${locale}`) ??
               get(articleDetails, 'identificazione.descrizione_marca.value') ?? '',
        acronym: get(articleDetails, 'identificazione.sigla_marca.value.code') ?? '',
        code: get(articleDetails, 'identificazione.codice_articolo.value') ?? '',
        code_description: get(articleDetails, `identificazione.descrizione_articolo.value.${locale}`) ?? '',
        operation: 'Sezione asset - download di un file',
        subject: JSON.stringify({
            File_name: fileName,
            File_type: fileType
        }),
        // subject: {
        //     File_name: fileName,
        //     File_type: fileType
        // },
    };

    //console.log('logAssetDownload payload:', logData);
    logAssetDownloadAnalytic(logData);
}

/**
 * Log per il download di un'esportazione FDS
 * Sempre attivo, non soggetto a parametro clientLogEnabled
 * @param {Object} exportRow - la riga dell'esportazione dalla tabella
 */
export const logFdsDownload = (exportRow) => {
    if (!exportRow) return;

    // Estrae il formato (name_channel)
    const formatCode = exportRow.info?.name_channel ?? 'unknown';

    // Estrae il tipo (Marca, Selezione, Collezione)
    const fileType = exportRow.type ?? 'unknown';

    // Estrae le marche da filter.marche (nomi estesi, es. "WOLF")
    const marche = exportRow.filter?.marche ?? [];

    // Estrae le sigle marca da info.filter.sigle_marca (acronimi, es. "WLF")
    const sigleMarca = exportRow.info?.filter?.sigle_marca ?? [];

    // Determina brand e acronym:
    // - Se una sola marca: brand = nome esteso, acronym = sigla
    // - Se più marche o nessuna: entrambi null
    // - Per type "Collezione": sempre null
    let brand = null;
    let acronym = null;

    if (fileType !== 'Collezione' && marche.length === 1) {
        brand = marche[0];
        acronym = sigleMarca[0] ?? null;
    }

    const logData = {
        brand: brand,
        acronym: acronym,
        code: null,
        code_description: null,
        operation: 'Export Marchi',
        subject: JSON.stringify({
            format_code: formatCode,
            File_name: '',
            File_type: fileType,
            Sigle_Marca: sigleMarca
        }),
        // subject: {
        //     format_code: formatCode,
        //     File_name: '',
        //     File_type: fileType,
        //     Sigle_Marca: sigleMarca
        // },
    };

    // DEBUG: stampa il payload prima di inviarlo
    //console.log('logFdsDownload payload:', logData);

    logAssetDownloadAnalytic(logData);
}

/**
 * Log per il download di un asset Pinaxo (Catalogo o Listino)
 * Sempre attivo, non soggetto a parametro clientLogEnabled
 * @param {Object} articleDetails - dettagli dell'articolo
 * @param {Object} item - l'item Pinaxo scaricato (contiene document, type, description, etc.)
 * @param {string} locale - locale corrente (es. 'it_IT')
 */
export const logAssetPinaxo = (articleDetails, item, locale = 'it_IT') => {
    if (!articleDetails || !item) return;

    // Usa la description del payload Pinaxo come nome file
    const fileName = item.description ?? '';

    // Mappa il tipo: "C" -> "Catalogo", "L" -> "Listino"
    const fileType = item.type === 'C' ? 'Catalogo' : item.type === 'L' ? 'Listino' : 'unknown';

    const logData = {
        brand: get(articleDetails, `identificazione.sigla_marca.value.label.${locale}`) ??
               get(articleDetails, 'identificazione.descrizione_marca.value') ?? '',
        acronym: get(articleDetails, 'identificazione.sigla_marca.value.code') ?? '',
        code: get(articleDetails, 'identificazione.codice_articolo.value') ?? '',
        code_description: get(articleDetails, `identificazione.descrizione_articolo.value.${locale}`) ?? '',
        operation: 'Sezione Pinaxo - download di un file',
        subject: JSON.stringify({
            File_name: fileName,
            File_type: fileType
        }),
    };

    //console.log('logAssetPinaxo payload:', logData);
    logAssetDownloadAnalytic(logData);
}

export const getFilteredAttrs = (section) => {
    return section.filter((a) => !includes(IGNORED_KEYS, a.key))
}

export const renderAttrValue = ({key, value}) => {
    if (typeof value === 'object' && value?.unit?.symbol) {
        return `${value.amount} ${value.unit.symbol}`; // Formatta con "amount" e "symbol"
    }

    if (isObject(value)) {
        return JSON.stringify(value)
    }

    return value
}

export const getFilteredAssetsAttributes = (props, stateSortBy, stateSortDirection) => {
    const { attributes, selectedFilter, extFilter } = props;

    if (selectedFilter === 'all' && extFilter === 'all' && stateSortBy === null) {
        return attributes;
    }

    let filteredAttributes = attributes;

    if (selectedFilter !== 'all') {
        filteredAttributes = filter(attributes, (attribute) => {
            const klasses = attribute.asset_classes.map((k) => k.code);

            return includes(klasses, selectedFilter);
        });
    }

    if (extFilter !== 'all') {
        filteredAttributes = filter(filteredAttributes, (attribute) => {
            return attribute.ext === extFilter;
        });
    }

    if (stateSortBy === null) {
        return filteredAttributes;
    }

    const sorted = sortBy(filteredAttributes, stateSortBy);

    if (stateSortDirection === 'asc') {
        return sorted;
    }

    return sorted.reverse();
}

export const getFilteredArticoliCorrelati = (props, stateSortBy, stateSortDirection) => {
    const { attributes, selectedFilter } = props;

    let filtered = attributes;

    if (selectedFilter !== 'all') {
        filtered = filter(filtered, (a) => {
            return a.type === selectedFilter;
        });
    }

    if (stateSortBy === null) {
        return sortBy(filtered, [TYPE_ATTR, CODICE_ARTICOLO_ATTR]);
    }

    const toSortBy =
        stateSortBy !== TYPE_ATTR ? stateSortBy : [TYPE_ATTR, CODICE_ARTICOLO_ATTR];

    const sorted = sortBy(filtered, toSortBy);

    if (stateSortDirection === 'asc') {
        return sorted;
    }

    return sorted.reverse();
}